import { Badge, DatePicker } from 'antd';
import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import styled from 'styled-components';
import { DateRangeSelector } from './StaffSchedule/DaysOffBox';
import OutlineAddStaff from 'assets/Icons/outlineAddStaff';
import settingStaffSelectors from 'features/settingStaff/services/selectors';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch } from 'store/hooks';
import settingStaffActions from 'features/settingStaff/services/actions';
import { useEffect, useState } from 'react';
import Close from 'assets/Icons/close';
import IcApt from 'assets/Icons/IcApt';
import useGetListDateOverrideStaff from 'features/settingStaff/services/hooks/useGetListDateOverrideStaff';
import { AxiosResponse } from 'axios';
import settingApis from 'features/settingStaff/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';

const StaffOverridesTimeOff = () => {
  const dispatch = useAppDispatch();
  const staffSelected = settingStaffSelectors.selectedStaffData.data();
  const disableDateList = settingStaffSelectors.selectedStaffData.dataDaysOff();
  const { listDateOverride, mutate } = useGetListDateOverrideStaff(staffSelected.id);

  const [rangeDate, setRangeDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [overrideDate, setOverrideDate] = useState<Dayjs | null>(null);

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    const currentStartOfDay = current?.startOf('day');
    return disableDateList.some(({ startTime, endTime }) => {
      const startDate: Dayjs = dayjs(startTime, 'MM-DD-YYYY').startOf('day');
      const endDate: Dayjs = dayjs(endTime, 'MM-DD-YYYY').startOf('day');
      return (
        currentStartOfDay &&
        (currentStartOfDay.isAfter(startDate) || currentStartOfDay.isSame(startDate)) &&
        (currentStartOfDay.isBefore(endDate) || currentStartOfDay.isSame(endDate))
      );
    });
  };

  useEffect(() => {
    if (staffSelected.id) {
      dispatch(settingStaffActions.staffScheduler.fetch(staffSelected.id));
      const params = {
        staffId: staffSelected.id,
      };
      dispatch(settingStaffActions.setStaffDayOffParams(params));
    }
  }, [staffSelected]);

  const handleCreateDayOff = () => {
    if (!rangeDate) return;
    const startTime = dayjs(rangeDate[0]).format('MM-DD-YYYY 00:00:00');
    const endTime = dayjs(rangeDate[1]).format('MM-DD-YYYY 23:59:00');
    const param = {
      staffId: staffSelected.id,
      startTime,
      endTime,
    };
    dispatch(settingStaffActions.createStaffDayOff.fetch(param));
    setRangeDate(null);
  };

  const handleDelete = (id: string) => {
    dispatch(settingStaffActions.deleteStaffDayOff.fetch(id));
  };

  const handleCreateOverrideDate = async () => {
    if (!overrideDate) return;

    try {
      const res: AxiosResponse = await settingApis.addDateOverride(staffSelected.id, overrideDate.format('MM/DD/YYYY'));

      if (res?.data?.data) {
        toast.success('Add override date successfully');
        mutate(staffSelected.id);
        setOverrideDate(null);
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Add override date failed');
    }
  };

  const handleDeleteOverrideDate = async (id: string) => {
    try {
      const res: AxiosResponse = await settingApis.deleteDateOverride(id);
      if (res?.data?.data) {
        toast.success('Delete override date successfully');
        mutate(staffSelected.id);
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Delete override date failed');
    }
  };

  return (
    <Container>
      <WorkDayOverrides className='work-day-overrides'>
        <Box display='flex' alignItems='center' gap='3'>
          <Text variant='H5'>Work Day Overrides</Text>
          <Badge count={listDateOverride?.length || 0} style={{ backgroundColor: '#D2464F' }} showZero />
        </Box>
        <Box display='flex' gap='3'>
          <DatePicker
            placeholder='mm-dd-yyyy'
            format='MM-DD-YYYY'
            disabledDate={disabledDate}
            onChange={setOverrideDate}
            value={overrideDate}
          />
          <Button
            ntype='PRIMARY'
            iconElement={<OutlineAddStaff width={24} height={24} />}
            size='middle'
            onClick={handleCreateOverrideDate}
            disabled={!overrideDate}
          >
            Create
          </Button>
        </Box>
        {listDateOverride && listDateOverride.length > 0 && (
          <Box display='flex' flexDirection='column' style={{ gap: '12px' }}>
            <Text variant='H8' color='text_3_v2' style={{ fontWeight: 500 }}>
              Selected Dates
            </Text>
            {listDateOverride.map((item) => (
              <Box
                key={item.id}
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                className='selected-date-item'
              >
                <Box display='flex' alignItems='center' gap='2'>
                  <IcApt width={24} height={24} fill='#232F3E' />
                  <Text variant='CONTENT_2'>{dayjs(item?.date).format('MM-DD-YYYY')}</Text>
                </Box>
                <button onClick={() => handleDeleteOverrideDate(item.id)} type='button'>
                  <Close width={20} height={20} fill='#D2464F' />
                </button>
              </Box>
            ))}
          </Box>
        )}
      </WorkDayOverrides>
      <DayOff className='day-off'>
        <Box display='flex' alignItems='center' gap='3'>
          <Text variant='H5'>Day Off</Text>
          <Badge count={disableDateList?.length || 0} style={{ backgroundColor: '#D2464F' }} showZero />
        </Box>
        <Box display='flex' gap='3'>
          <DateRangeSelector
            disabledDate={disabledDate}
            placement='bottomLeft'
            value={rangeDate}
            onChange={(dates) => {
              if (dates?.[0] && dates?.[1]) {
                setRangeDate([dates[0], dates[1]]);
              } else {
                setRangeDate(null);
              }
            }}
          />
          <Button
            ntype='PRIMARY'
            iconElement={<OutlineAddStaff width={24} height={24} />}
            onClick={handleCreateDayOff}
            disabled={!rangeDate}
          >
            Create
          </Button>
        </Box>
        {disableDateList.length > 0 && (
          <Box display='flex' flexDirection='column' style={{ gap: '12px' }}>
            <Text variant='H8' color='text_3_v2' style={{ fontWeight: 500 }}>
              Selected Dates
            </Text>
            {disableDateList.map((item) => (
              <Box
                key={item.id}
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                className='selected-date-item'
              >
                <Box display='flex' alignItems='center' gap='2'>
                  <IcApt width={24} height={24} fill='#232F3E' />
                  <Text variant='CONTENT_2'>{dayjs(item?.startTime).format('MM-DD-YYYY')}</Text>
                </Box>
                <button onClick={() => handleDelete(item.id)} type='button'>
                  <Close width={20} height={20} fill='#D2464F' />
                </button>
              </Box>
            ))}
          </Box>
        )}
      </DayOff>
    </Container>
  );
};

export default StaffOverridesTimeOff;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  font-family: Poppins;

  .work-day-overrides,
  .day-off {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 24px;
    border: 1px solid #ccd4dc;
  }
`;

const WorkDayOverrides = styled.div`
  .CONTENT_2 {
    font-size: 18px;
  }

  .ant-picker {
    height: 3.5rem;
    border-radius: 4px;
    border: 1px solid #adadad;
  }

  .selected-date-item {
    padding: 16px;
    border-radius: 8px;
    border-bottom: 1px solid #e9edf2;
    background: #f6f7fc;
  }
`;

const DayOff = styled.div`
  .CONTENT_2 {
    font-size: 18px;
  }

  .ant-picker {
    height: 3.5rem;
  }

  .selected-date-item {
    padding: 16px;
    border-radius: 8px;
    border-bottom: 1px solid #e9edf2;
    background: #f6f7fc;
  }
`;
